import React, { useEffect, useMemo, useState } from 'react';

import ExcelJS from 'exceljs';

import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import Pagination from '../../components/Pagination.tsx';
import LoaderOverlay from '../../components/LoaderOverlay';

import layoutStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import reportStyles from '../../styles/ReportesIngresosDiarios.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';

import { getCampusDropdown } from '../../api/aplicacionPagos';
import { getReporteAdeudos } from '../../api/reportesFinancieros';

const ReporteAdeudosScreen = () => {
  const { isCollapsed } = useSidebar();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampus, setSelectedCampus] = useState(() => localStorage.getItem('selectedCampus') || '');
  const [selectedSeccion, setSelectedSeccion] = useState('');
  const [campusId, setCampusId] = useState(null);
  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const handler = (e) => {
      const campus = (e.detail && e.detail.campus) || localStorage.getItem('selectedCampus') || '';
      setSelectedCampus(campus);
    };

    window.addEventListener('campusChanged', handler);
    setSelectedCampus(localStorage.getItem('selectedCampus') || '');

    return () => window.removeEventListener('campusChanged', handler);
  }, []);

  const resolveCampusId = async (campusName) => {
    if (!campusName || campusName === 'Todos' || campusName === 'Todos los Campus' || campusName === 'all') {
      return null;
    }

    let campusList = [];
    const cached = localStorage.getItem('aplicacionPagosCampusDropdownCache');
    if (cached) {
      try {
        campusList = JSON.parse(cached) || [];
      } catch {
        campusList = [];
      }
    }

    if (!Array.isArray(campusList) || campusList.length === 0) {
      campusList = await getCampusDropdown();
      if (Array.isArray(campusList)) {
        localStorage.setItem('aplicacionPagosCampusDropdownCache', JSON.stringify(campusList));
      }
    }

    const campusObj = (Array.isArray(campusList) ? campusList : []).find((c) =>
      c.nombre === campusName ||
      c.nombre_campus === campusName ||
      String(c.id) === String(campusName) ||
      String(c.id_campus) === String(campusName)
    );

    return campusObj ? (campusObj.id || campusObj.id_campus || null) : null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const resolvedCampusId = await resolveCampusId(selectedCampus);
        setCampusId(resolvedCampusId);

        const response = await getReporteAdeudos({
          campusId: resolvedCampusId || undefined,
          search: searchTerm.trim() || undefined,
        });

        setRows(Array.isArray(response?.results) ? response.results : []);
      } catch (err) {
        console.error('Error al cargar reporte de adeudos:', err);
        setError('Error al cargar el reporte de adeudos');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCampus, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [selectedCiclo, searchTerm, selectedCampus, selectedSeccion]);

  const getSeccionValue = (row) => row.seccion_nombre || row.seccion || row.nombre_seccion || '';

  const cicloOptions = useMemo(() => {
    const unique = new Set(
      rows
        .map((row) => String(row.ciclo_escolar || '').trim())
        .filter(Boolean)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const seccionOptions = useMemo(() => {
    const unique = new Set(
      rows
        .map((row) => String(getSeccionValue(row) || '').trim())
        .filter(Boolean)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const rowCiclo = String(row.ciclo_escolar || '').trim();
      const rowSeccion = String(getSeccionValue(row) || '').trim();

      const matchesCiclo = !selectedCiclo || rowCiclo === selectedCiclo;
      const matchesSeccion = !selectedSeccion || rowSeccion === selectedSeccion;

      return matchesCiclo && matchesSeccion;
    });
  }, [rows, selectedCiclo, selectedSeccion]);

  const paginatedRows = useMemo(() => filteredRows.slice((page - 1) * pageSize, page * pageSize), [filteredRows, page]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const formatMoney = (value) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const totalMontoNeto = filteredRows.reduce((acc, row) => acc + Number(row.monto_neto || 0), 0);

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Reporte adeudos');

    const NUM_COLS = 12;
    const BLUE_BG = 'FF4472C4'; // exceljs requiere ARGB
    const WHITE_FG = 'FFFFFFFF';

    // Título
    worksheet.addRow(['REPORTE DE ADEUDOS', ...Array(NUM_COLS - 1).fill('')]);
    worksheet.addRow(Array(NUM_COLS).fill(''));
    worksheet.addRow([
      `CICLO ESCOLAR  ${selectedCiclo || ''}`,
      '', '',
      `CAMPUS  ${selectedCampus || ''}`,
      '', '',
      `SECCIÓN  ${selectedSeccion || ''}`,
      '', '', '',
      `PROGRAMA  `,
      '',
    ]);
    worksheet.addRow(Array(NUM_COLS).fill(''));

    // Encabezados
    worksheet.addRow([
      '#',
      'Matrícula',
      'Nombre',
      'Ciclo Escolar',
      'Fecha Vencimiento',
      'Concepto',
      'Descripción',
      'Monto Original',
      'Descuento Beca',
      'Descuento Bonificación',
      'Descuento Condonación',
      'Monto Neto',
    ]);

    filteredRows.forEach((row, idx) => {
      worksheet.addRow([
        idx + 1,
        row.matricula || '',
        row.nombre || '',
        row.ciclo_escolar || '',
        row.fecha_vencimiento || '',
        row.concepto || '',
        row.descripcion || '',
        Number(row.monto_original || 0),
        Number(row.descuento_beca_monto || 0),
        Number(row.descuento_bonificacion || 0),
        Number(row.descuento_condonacion || 0),
        Number(row.monto_neto || 0),
      ]);
    });

    // Fila TOTAL
    const totalOriginal = filteredRows.reduce((a, r) => a + Number(r.monto_original || 0), 0);
    const totalBeca     = filteredRows.reduce((a, r) => a + Number(r.descuento_beca_monto || 0), 0);
    const totalBonif    = filteredRows.reduce((a, r) => a + Number(r.descuento_bonificacion || 0), 0);
    const totalCond     = filteredRows.reduce((a, r) => a + Number(r.descuento_condonacion || 0), 0);

    worksheet.addRow([
      '', '', '', '', '', '',
      'TOTAL',
      Number(totalOriginal.toFixed(2)),
      Number(totalBeca.toFixed(2)),
      Number(totalBonif.toFixed(2)),
      Number(totalCond.toFixed(2)),
      Number(totalMontoNeto.toFixed(2)),
    ]);

    // Combinar celdas para el título
    worksheet.mergeCells(1, 1, 1, NUM_COLS);

    // Anchos de columna
    const widths = [
      { width: 5 },  // #
      { width: 12 }, // Matrícula
      { width: 28 }, // Nombre
      { width: 15 }, // Ciclo Escolar
      { width: 18 }, // Fecha Vencimiento
      { width: 12 }, // Concepto
      { width: 30 }, // Descripción
      { width: 14 }, // Monto Original
      { width: 15 }, // Descuento Beca
      { width: 20 }, // Descuento Bonificación
      { width: 20 }, // Descuento Condonación
      { width: 14 }, // Monto Neto
    ];
    worksheet.columns = widths;

    // Estilos
    // Título
    const titleCell = worksheet.getCell('A1');
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // Encabezados (fila 5)
    const headerRow = worksheet.getRow(5);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: WHITE_FG } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE_BG } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: WHITE_FG } },
        bottom: { style: 'thin', color: { argb: WHITE_FG } },
        left: { style: 'thin', color: { argb: WHITE_FG } },
        right: { style: 'thin', color: { argb: WHITE_FG } },
      };
    });

    // Formato de moneda para columnas de montos
    // Columnas: 8 (Monto Original), 12 (Monto Neto) (1-based)
    for (let i = 6; i <= worksheet.rowCount; i++) { // desde encabezados hasta total
      worksheet.getRow(i).getCell(8).numFmt = '[$$-409]#,##0.00';
      worksheet.getRow(i).getCell(12).numFmt = '[$$-409]#,##0.00';
    }

    // Fila TOTAL (última)
    const totalRowIdx = worksheet.rowCount;
    const totalRow = worksheet.getRow(totalRowIdx);
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, color: { argb: WHITE_FG } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE_BG } };
      cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'right', vertical: 'middle' };
    });

    // Descargar archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'reporte_adeudos.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    handleDownloadExcel();
  };

  return (
    <div className={layoutStyles.mainContainer} style={{ height: '100vh', display: 'flex' }}>
      <div className={`${layoutStyles.layout} ${isCollapsed ? layoutStyles.sidebarCollapsedLayout : ''}`} style={{ flex: 1, display: 'flex', height: '100vh' }}>
        <div className={layoutStyles.sidebarColumn} style={{ height: '100vh', minHeight: 0 }}>
          <Sidebar />
        </div>

        <div className={layoutStyles.contentColumn} style={{ position: 'relative', height: '100vh', overflowY: 'auto', minHeight: 0 }}>
          <LoaderOverlay visible={loading} label="Cargando reporte de adeudos..." />

          <HeaderGlobal
            title="Reporte de Adeudos"
            campus={selectedCampus}
            onCampusChange={(e) => {
              const campus = e.target?.value || '';
              setSelectedCampus(campus);
            }}
          />

          <main className={layoutStyles.mainContent}>
            <div className={layoutStyles.contentArea}>
              <div className={estudiantesStyles.searchSection}>
                <div className={estudiantesStyles.searchCard}>
                  <div className={estudiantesStyles.searchRow}>
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Ciclo escolar</label>
                      <select
                        className={estudiantesStyles.searchInput}
                        value={selectedCiclo}
                        onChange={(e) => setSelectedCiclo(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {cicloOptions.map((ciclo) => (
                          <option key={ciclo} value={ciclo}>{ciclo}</option>
                        ))}
                      </select>
                    </div>

                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Buscar</label>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Matrícula, nombre, concepto o descripción"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Seccion</label>
                      <select
                        className={estudiantesStyles.searchInput}
                        value={selectedSeccion}
                        onChange={(e) => setSelectedSeccion(e.target.value)}
                      >
                        <option value="">Todas</option>
                        {seccionOptions.map((seccion) => (
                          <option key={seccion} value={seccion}>{seccion}</option>
                        ))}
                      </select>
                    </div>

                    <div className={reportStyles.filterActions}>
                      <button
                        type="button"
                        className={reportStyles.clearFiltersButton}
                        onClick={() => {
                          setSelectedCiclo('');
                          setSearchTerm('');
                          setSelectedSeccion('');
                        }}
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`${layoutStyles.contentWrapper} ${reportStyles.reportWrapper}`}>
                <div className={reportStyles.reportContainer}>
                  <div className={reportStyles.actionsBar}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        type="button"
                        className={`${reportStyles.downloadButton} bg-blue-900 text-white border-transparent`}
                        onClick={handlePrint}
                        style={{ backgroundColor: '#1e3a8a', color: '#ffffff', borderColor: 'transparent' }}
                      >
                        <span className={reportStyles.downloadIcon}>↧</span>
                        <span>Exportar Excel</span>
                      </button>
                    </div>
                  </div>

                  <header className={reportStyles.reportHeader}>
                    <div className={reportStyles.headerLeft}>
                      <div className={reportStyles.logoPlaceholder} />
                    </div>
                    <div className={reportStyles.headerCenter}>
                      <h2 className={reportStyles.institutionName}>Centro De Estudios Superiores CMP, A.C.</h2>
                      <div className={reportStyles.institutionAddress}>
                        Ave. Venustiano Carranza Nte. No.Ext. 614 CENTRO CP 64000 MONTERREY, NUEVO LEÓN
                      </div>
                      <div className={reportStyles.reportTitleRow}>
                        <span>Reporte de adeudos</span>
                        <span className={reportStyles.reportDate}>Campus: {selectedCampus || 'Todos'}</span>
                      </div>
                    </div>
                  </header>

                  {error && (
                    <div style={{ marginBottom: 12, color: '#b91c1c', textAlign: 'center' }}>{error}</div>
                  )}

                  {selectedCampus && !campusId && (
                    <div style={{ marginBottom: 12, color: '#b45309', textAlign: 'center' }}>
                      El campus seleccionado no pudo resolverse a un ID válido. Se muestran resultados sin filtro por campus.
                    </div>
                  )}

                  <section className={reportStyles.tableSection}>
                    <div className={reportStyles.tableScrollWrapper}>
                      <table className={reportStyles.dataTable}>
                        <thead>
                          <tr>
                            <th>Matrícula</th>
                            <th>Campus</th>
                            <th>Nombre</th>
                            <th>Ciclo Escolar</th>
                            <th>Fecha Vencimiento</th>
                            <th>Concepto</th>
                            <th>Descripción</th>
                            <th className={reportStyles.numeric}>Monto Original</th>
                            <th className={reportStyles.numeric}>Descuento Beca</th>
                            <th className={reportStyles.numeric}>Descuento Bonificación</th>
                            <th className={reportStyles.numeric}>Descuento Condonación</th>
                            <th className={reportStyles.numeric}>Monto Neto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedRows.map((row, idx) => (
                            <tr key={row.id_cargo || `${row.matricula}-${idx}`}>
                              <td className={reportStyles.center}>{row.matricula}</td>
                              <td className={reportStyles.center}>{row.campus || selectedCampus || 'N/A'}</td>
                              <td>{row.nombre}</td>
                              <td className={reportStyles.center}>{row.ciclo_escolar}</td>
                              <td className={reportStyles.center}>{row.fecha_vencimiento}</td>
                              <td className={reportStyles.center}>{row.concepto}</td>
                              <td>{row.descripcion}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.monto_original)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.descuento_beca_monto)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.descuento_bonificacion)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.descuento_condonacion)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.monto_neto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {!loading && filteredRows.length === 0 && (
                      <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '0.9rem', color: '#777' }}>
                        No hay adeudos para los filtros seleccionados.
                      </div>
                    )}
                  </section>

                  {filteredRows.length > pageSize && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center' }}>
                      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
                    </div>
                  )}

                  <section className={reportStyles.summarySection}>
                    <div className={reportStyles.summaryRow}>
                      <span className={reportStyles.summaryLabel}>TOTAL MONTO NETO</span>
                      <span className={reportStyles.summaryValue}>{formatMoney(totalMontoNeto)}</span>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReporteAdeudosScreen;
